/**
 * Getting the signature out of the browser: rich copy, plain copy, file download.
 *
 * Browser-only and therefore untested under the node Jest setup — which is why it holds no logic
 * worth testing: every byte it moves was produced and linted by the pure modules beside it.
 */

/**
 * Copies the signature as RICH TEXT, with a plain-text alternative on the same clipboard entry.
 *
 * Rich is what every signature editor that takes a paste wants (iOS Mail, new Outlook): it
 * receives rendered formatting, not markup. The async clipboard API is tried first; where it is
 * missing (Firefox before 127, a non-secure origin) the fragment is rendered off-screen, selected
 * and copied the old way, which puts the same `text/html` on the clipboard.
 *
 * @returns Whether the browser reported success.
 */
export async function copyRichText(
  html: string,
  text: string,
): Promise<boolean> {
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        }),
      ]);
      return true;
    } catch {
      // Permission refused or unsupported type — fall through to the selection path.
    }
  }
  const host = document.createElement('div');
  host.setAttribute('contenteditable', 'true');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  // The fragment is our own renderer's escaped, linted output — never raw operator input.
  host.innerHTML = html;
  document.body.appendChild(host);
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(host);
  selection?.removeAllRanges();
  selection?.addRange(range);
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } finally {
    selection?.removeAllRanges();
    host.remove();
  }
  return copied;
}

/** Copies plain text — the HTML source, or the plain-text signature. */
export async function copyPlainText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through.
    }
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.style.position = 'fixed';
  area.style.left = '-10000px';
  document.body.appendChild(area);
  area.select();
  let copied = false;
  try {
    copied = document.execCommand('copy');
  } finally {
    area.remove();
  }
  return copied;
}

/** Saves a string as a file. */
export function downloadTextFile(
  fileName: string,
  content: string,
  mimeType: string,
): void {
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next tick: revoking synchronously cancels the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
